import { RemoveCircle } from 'components/icons';
import CircularLoader from 'components/CircularLoader';
import { Tooltip } from 'components/ui/tooltip';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Controller, FieldValues, SetFieldValue, useFormContext, useWatch } from 'react-hook-form';
import axios from 'utils/axios';
import { t } from 'utils/getTranslations';
import { cn } from 'utils/cn';
import Avatar from '../../../@extended/Avatar';
import CropDialog from './CropDialog';
import { CommonInputProps } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useMemoizedRules } from './useMemoizedRules';

interface Props<T extends FieldValues> extends CommonInputProps<T> {
    setValue: SetFieldValue<T>;
    onSuccess?: () => void;
    onError?: () => void;
    accept?: string;
    variant?: 'circular' | 'rounded' | 'square';
    width?: number;
    height?: number;
    imageHoverText?: string;
    noImageHoverText?: string;
    removeImageHoverText?: string;
    uploadEndpoint?: string;
    downloadEndpoint?: string;
    maxWidth?: number;
    maxHeight?: number;
    fallbackIcon?: React.ReactNode;
    enableCrop?: boolean;
    cropAspect?: number;
    pendingUploadRef?: React.MutableRefObject<(() => Promise<string | null>) | null>;
}

const validateImageDimensions = (
    file: File,
    maxWidth?: number,
    maxHeight?: number
): Promise<{ valid: true } | { valid: false; width: number; height: number }> => {
    return new Promise((resolve) => {
        // If no limits, always valid
        if (!maxWidth && !maxHeight) {
            resolve({ valid: true });
            return;
        }

        const img = new Image();
        img.onload = () => {
            const { width, height } = img;
            URL.revokeObjectURL(img.src); // Cleanup

            const widthExceeds = maxWidth && width > maxWidth;
            const heightExceeds = maxHeight && height > maxHeight;

            if (widthExceeds || heightExceeds) {
                resolve({ valid: false, width, height });
            } else {
                resolve({ valid: true });
            }
        };

        img.onerror = () => {
            URL.revokeObjectURL(img.src);
            resolve({ valid: false, width: 0, height: 0 });
        };

        img.src = URL.createObjectURL(file);
    });
};

const buildDimensionErrorMessage = (
    actualWidth: number,
    actualHeight: number,
    maxWidth?: number,
    maxHeight?: number
): string => {
    const widthExceeds = maxWidth && actualWidth > maxWidth;
    const heightExceeds = maxHeight && actualHeight > maxHeight;

    if (widthExceeds && heightExceeds) {
        return t('validation.image-dimensions-exceed-both', {
            width: actualWidth.toString(),
            height: actualHeight.toString(),
            maxWidth: maxWidth.toString(),
            maxHeight: maxHeight.toString(),
        });
    } else if (widthExceeds) {
        return t('validation.image-width-exceeds-max', {
            width: actualWidth.toString(),
            maxWidth: maxWidth.toString(),
        });
    } else if (heightExceeds) {
        return t('validation.image-height-exceeds-max', {
            height: actualHeight.toString(),
            maxHeight: maxHeight.toString(),
        });
    }
    return t('validation.image-dimensions-invalid');
};

const ImageInput = <T extends FieldValues>({
    name,
    disabled,
    label,
    required,
    validate,
    control,
    setValue,
    accept = '.bmp, .gif, .jpeg, .jpg, .png, .tiff, .wbmp',
    onError,
    onSuccess,
    variant = 'circular',
    imageHoverText = t('image-input.edit-image'),
    noImageHoverText = t('image-input.add-image'),
    removeImageHoverText = t('image-input.remove'),
    uploadEndpoint,
    downloadEndpoint,
    width = 150,
    height = 150,
    maxWidth,
    maxHeight,
    fallbackIcon,
    enableCrop = false,
    cropAspect,
    pendingUploadRef,
}: Props<T>) => {
    const [source, setSource] = useState<string | undefined>(undefined);
    const [isHovered, setIsHovered] = useState(false);
    const [isRemovalHovered, setIsRemovalHovered] = useState(false);
    // TODO(react-doctor: rendering-usetransition-loading): `isLoading` guards an
    // async axios fetch (network round-trip), not a React state transition.
    // useTransition is the wrong tool here. Intentionally kept as useState.
    const [isLoading, setIsLoading] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
    const pendingFileInputRef = useRef<HTMLInputElement | null>(null);
    const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);
    const sourceRef = useRef<string | undefined>(undefined);
    const pendingBlobRef = useRef<Blob | null>(null);
    const rules = useMemoizedRules({ required, validate });
    const value = useWatch({ control, name });
    const { setError, clearErrors } = useFormContext<T>();

    const updateSource = (url: string | undefined) => {
        if (sourceRef.current) URL.revokeObjectURL(sourceRef.current);
        sourceRef.current = url;
        setSource(url);
    };

    useEffect(() => {
        return () => {
            if (sourceRef.current) URL.revokeObjectURL(sourceRef.current);
        };
    }, []);

    // TODO(react-doctor: no-fetch-in-effect): deliberate raw axios fetch — the
    // download streams a binary blob and converts to an object URL for an <img>
    // tag, which RTK Query's JSON-oriented cache layer is a poor fit for.
    // Intentionally kept; do not migrate to RTK Query.
    useEffect(() => {
        const fetchImage = async () => {
            setIsLoading(true);
            try {
                if (value) {
                    const response = await axios.get(`${import.meta.env.VITE_API_URL}${downloadEndpoint}${value}`, {
                        withCredentials: true,
                        responseType: 'blob',
                    });
                    const blob = response.data;
                    updateSource(URL.createObjectURL(blob));
                } else if (!pendingBlobRef.current) {
                    updateSource(undefined);
                }
            } catch {
                onError?.();
            } finally {
                setIsLoading(false);
            }
        };
        fetchImage();
    }, [value, downloadEndpoint, onError]);

    // TODO(react-doctor: no-fetch-in-effect): exposes a deferred raw multipart
    // upload (presigned-style) via `pendingUploadRef` for the parent form to
    // invoke on submit. The fetch is not invoked here — the effect just wires
    // the imperative callback. Intentionally kept; do not migrate to RTK Query.
    useEffect(() => {
        if (!pendingUploadRef) return;
        pendingBlobRef.current = pendingBlob;
        if (pendingBlob) {
            pendingUploadRef.current = async () => {
                const blob = pendingBlobRef.current;
                if (!blob) return null;
                const formData = new FormData();
                formData.append('files', blob, 'profile.png');
                const response = await axios.post(`${import.meta.env.VITE_API_URL}${uploadEndpoint}`, formData, {
                    withCredentials: true,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                const uploadedFileName = response.data.results[0].uploadedFileName;
                pendingBlobRef.current = null;
                setPendingBlob(null);
                return uploadedFileName;
            };
        } else {
            pendingUploadRef.current = null;
        }
    }, [pendingBlob, pendingUploadRef, uploadEndpoint]);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const image = event.target.files?.[0] || null;
        if (!image) return;

        clearErrors(name);

        if (enableCrop) {
            const objectUrl = URL.createObjectURL(image);
            setCropImageSrc(objectUrl);
            pendingFileInputRef.current = event.target;
            return;
        }

        // Validate dimensions
        const result = await validateImageDimensions(image, maxWidth, maxHeight);

        if (!result.valid) {
            const errorMessage = buildDimensionErrorMessage(result.width, result.height, maxWidth, maxHeight);
            setError(name, { type: 'dimensions', message: errorMessage });
            event.target.value = '';
            return;
        }

        // Proceed with upload if validation passed
        setIsLoading(true);
        const formData = new FormData();
        formData.append('files', image);
        try {
            const response = await axios.post(`${import.meta.env.VITE_API_URL}${uploadEndpoint}`, formData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            const data = await response.data;
            setValue(name, data.results[0].uploadedFileName);
            updateSource(URL.createObjectURL(image));
            event.target.files = new DataTransfer().files;
            onSuccess?.();
        } catch {
            onError?.();
        } finally {
            setIsLoading(false);
        }
    };

    const cleanupCropState = () => {
        if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
        setCropImageSrc(null);
        if (pendingFileInputRef.current) {
            pendingFileInputRef.current.value = '';
            pendingFileInputRef.current = null;
        }
    };

    const handleCropConfirm = (croppedBlob: Blob) => {
        cleanupCropState();
        setPendingBlob(croppedBlob);
        updateSource(URL.createObjectURL(croppedBlob));
    };

    const handleCropClose = () => {
        cleanupCropState();
    };

    const handleRemove = (e: React.MouseEvent) => {
        if (disabled) return;
        setIsRemovalHovered(false);
        e.preventDefault();
        setValue(name, null);
        updateSource(undefined);
        setPendingBlob(null);
    };

    const hasImage = !!value || !!pendingBlob;

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field: { ref }, fieldState: { error } }) => {
                return (
                    <InputWrapper
                        name={name}
                        label={label}
                        hasErrors={!!error}
                        error={error}
                        readonly={disabled}
                        className="py-2"
                    >
                        <div className="flex flex-col gap-2">
                            <input
                                type="file"
                                accept={accept}
                                ref={ref}
                                disabled={disabled}
                                onChange={(e) => {
                                    void handleFileChange(e);
                                }}
                                id={`file-input-${name}`}
                                // Visually hidden but still focusable, so the picker opens from the keyboard;
                                // a `hidden` input leaves the label as the only affordance and labels take no focus.
                                className="peer sr-only"
                            />
                            <label
                                htmlFor={`file-input-${name}`}
                                className="peer-focus-visible:outline-primary w-fit peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2"
                            >
                                <div
                                    className="relative inline-block"
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}
                                >
                                    <Avatar
                                        // Inset hairline so light photo edges don't bleed into the page
                                        className={cn(
                                            'outline-grey-900/10 outline -outline-offset-1 transition-[filter] duration-300',
                                            disabled ? 'cursor-default' : 'cursor-pointer',
                                            isHovered && hasImage && 'brightness-90'
                                        )}
                                        style={{ height, width }}
                                        src={source}
                                        variant={variant}
                                    >
                                        {fallbackIcon}
                                    </Avatar>
                                    {/* The badge corner: the spinner while a fetch is in flight, otherwise the
                                        remove control, which only fades in under the pointer. */}
                                    <span
                                        className={cn(
                                            'absolute right-0 bottom-0 flex size-5 items-center justify-center transition-opacity duration-300',
                                            isHovered || isLoading ? 'opacity-100' : 'opacity-0'
                                        )}
                                    >
                                        {isLoading ? (
                                            <CircularLoader className="static [&>span]:size-5" />
                                        ) : (
                                            hasImage &&
                                            !disabled && (
                                                <Tooltip content={removeImageHoverText}>
                                                    <button
                                                        type="button"
                                                        aria-label={removeImageHoverText ?? t('remove')}
                                                        onMouseEnter={() => setIsRemovalHovered(true)}
                                                        onMouseLeave={() => setIsRemovalHovered(false)}
                                                        onClick={handleRemove}
                                                        className="focus-visible:outline-primary flex cursor-pointer items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2"
                                                    >
                                                        <RemoveCircle className="text-fg-secondary" />
                                                    </button>
                                                </Tooltip>
                                            )
                                        )}
                                    </span>
                                    {isHovered && !isRemovalHovered && !disabled && (
                                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                            <span className="bg-backdrop text-grey-0 rounded-sm px-2 py-1 text-sm">
                                                {hasImage ? imageHoverText : noImageHoverText}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </label>

                            {cropImageSrc && (
                                <CropDialog
                                    imageSrc={cropImageSrc}
                                    onClose={handleCropClose}
                                    onConfirm={handleCropConfirm}
                                    aspect={cropAspect ?? (variant === 'circular' ? 1 : width / height)}
                                    cropShape={variant === 'circular' ? 'round' : 'rect'}
                                />
                            )}
                        </div>
                    </InputWrapper>
                );
            }}
        />
    );
};

export default ImageInput;
